import React, { useEffect, useState } from 'react';
import { Search, ArrowUpDown, Download, Loader, Filter, User, DollarSign, Info } from 'lucide-react';

const AllUsersTable = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
    const [hoveredRow, setHoveredRow] = useState(null);

    // Function to safely convert values to numbers
    const toNumber = (val) => Number(val || 0);

    // Fetch users data on component mount
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                // Using fetch instead of axios to avoid dependency
                const res = await fetch('https://api.happychd.com/api/user');
                const data = await res.json();
                setUsers(data || []);
            } catch (err) {
                console.error('Error fetching users:', err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchUsers();
    }, []);

    // Function to handle sorting
    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    // Sort and filter users based on current configuration
    const sortedUsers = React.useMemo(() => {
        let sortableUsers = [...users];

        // Filter by search term
        if (searchTerm) {
            sortableUsers = sortableUsers.filter(user =>
                (user.name && user.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (user.userId && user.userId.toString().includes(searchTerm))
            );
        }

        // Sort by selected column
        if (sortConfig.key) {
            sortableUsers.sort((a, b) => {
                // Computing all values first for proper sorting
                const userData1 = computeUserData(a);
                const userData2 = computeUserData(b);

                let valueA = sortConfig.key.includes('.')
                    ? userData1[sortConfig.key.split('.')[1]]
                    : userData1[sortConfig.key];

                let valueB = sortConfig.key.includes('.')
                    ? userData2[sortConfig.key.split('.')[1]]
                    : userData2[sortConfig.key];

                if (typeof valueA === 'string' && typeof valueB === 'string') {
                    valueA = valueA.toLowerCase();
                    valueB = valueB.toLowerCase();
                }

                if (valueA < valueB) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (valueA > valueB) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }

        return sortableUsers;
    }, [users, sortConfig, searchTerm]);

    // Calculate all derived values for a user
    const computeUserData = (user) => {
        const {
            name,
            userId,
            commission = 0,
            idPercentage = 0,
            transactions = [],
            paymentStatus = [],
            balanceHistory = [],
        } = user;

        const totalDeposit = transactions
            .filter((t) => t.type === 'deposit')
            .reduce((sum, t) => sum + toNumber(t.amount), 0);

        const totalWithdraw = transactions
            .filter((t) => t.type === 'withdraw')
            .reduce((sum, t) => sum + toNumber(t.amount), 0);

        const totalPaymentStatus = paymentStatus.reduce((sum, p) => sum + toNumber(p.amount), 0);
        const totalBalance = balanceHistory.reduce((sum, b) => sum + toNumber(b.amount), 0);

        const rawIdSettled = (totalWithdraw + totalBalance - totalDeposit) * (idPercentage / 100);
        const cmsnSettled = rawIdSettled < 0 ? Math.abs(rawIdSettled * (commission / 100)) : 0;

        const idSettled = (rawIdSettled + cmsnSettled);
        const netSettlement = totalPaymentStatus + idSettled;

        return {
            name,
            userId,
            idPercentage,
            commission,
            totalPaymentStatus,
            totalDeposit,
            totalWithdraw,
            totalBalance,
            rawIdSettled,
            cmsnSettled,
            idSettled,
            netSettlement
        };
    };


    // Export data as CSV
    const exportToCSV = () => {
        const headers = [
            'Sr.No.',
            'Name',
            'Payment Status',
            'ID',
            'ID %',
            'Commission %',
            'Total Deposit',
            'Total Withdraw',
            'Balance',
            'Raw ID Settled',
            'Cmsn Settled',
            'ID Settled',
            'Net Settlement'
        ];

        const rows = sortedUsers.map((user, idx) => {
            const data = computeUserData(user);
            return [
                idx + 1,
                data.name || 'N/A',
                data.totalPaymentStatus.toFixed(2),
                data.userId,
                data.idPercentage,
                data.commission,
                data.totalDeposit.toFixed(2),
                data.totalWithdraw.toFixed(2),
                data.totalBalance.toFixed(2),
                data.rawIdSettled.toFixed(2),
                data.cmsnSettled.toFixed(2),
                data.idSettled.toFixed(2),
                data.netSettlement.toFixed(2)
            ];
        });

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', 'users_data.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Loading state with spinner
    if (loading) {
        return (
            <div className="p-6 flex flex-col items-center justify-center h-64 text-gray-600">
                <Loader className="animate-spin h-8 w-8 mb-4 text-orange-500" />
                <p className="text-lg">Loading user data...</p>
            </div>
        );
    }

    // Table column definitions
    const columns = [
        { key: 'index', label: 'Sr.No.', sortable: true },
        { key: 'name', label: 'Name', sortable: true },
        { key: 'userId', label: 'ID', sortable: true },
        { key: 'totalPaymentStatus', label: 'Payment Status', sortable: true },
        { key: 'idPercentage', label: 'ID %', sortable: true },
        { key: 'commission', label: 'Commission %', sortable: true },
        { key: 'totalDeposit', label: 'Total Deposit', sortable: true },
        { key: 'totalWithdraw', label: 'Total Withdraw', sortable: true },
        { key: 'totalBalance', label: 'Balance Withdrawal', sortable: true },
        { key: 'rawIdSettled', label: 'Raw ID Settled', sortable: true },
        { key: 'cmsnSettled', label: 'Cmsn Settled', sortable: true },
        { key: 'idSettled', label: 'ID Settled', sortable: true },
        { key: 'netSettlement', label: 'Net Settlement', sortable: true }
    ];

    // Format currency values
    const formatCurrency = (value) => {
        return `₹${value.toFixed(2)}`;
    };

    const groupByReferrer = (items) => {
        const groups = {};

        items.forEach((item) => {
            const refName = item.referredBy?.userId || 'Unassigned';
            if (!groups[refName]) {
                groups[refName] = [];
            }
            groups[refName].push(item);
        });

        return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
    };

    const groupByName = (items) => {
  const groups = {};

  items.forEach((item) => {
    const name = item.name || 'Unnamed';
    if (!groups[name]) groups[name] = [];
    groups[name].push(item);
  });

  return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
};

    return (
        <div className="bg-white rounded-lg shadow-lg p-6">
            {/* Header with search and export */}
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
                <div className="flex items-center mb-4 md:mb-0">
                    <User className="h-8 w-8 text-orange-500 mr-3" />
                    <h2 className="text-2xl font-bold text-gray-800">
                        Client Summary <span className="text-orange-500">({sortedUsers.length})</span>
                    </h2>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search users..."
                            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    </div>

                    <button
                        onClick={exportToCSV}
                        className="flex items-center justify-center bg-orange-50 hover:bg-orange-100 text-orange-600 px-4 py-2 rounded-lg border border-orange-200 transition-colors"
                    >
                        <Download className="h-4 w-4 mr-2" />
                        Export CSV
                    </button>
                </div>
            </div>

            {/* Stats cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {[
                    {
                        title: 'Total Deposits',
                        value: `₹${users.reduce((sum, user) => {
                            const data = computeUserData(user);
                            return sum + data.totalDeposit;
                        }, 0).toFixed(2)}`,
                        icon: <span className="h-6 w-6 text-green-500 text-xl">₹</span>,
                        color: 'bg-green-50 text-green-700 border-green-100'
                    },
                    {
                        title: 'Total Withdrawals',
                        value: `₹${users.reduce((sum, user) => {
                            const data = computeUserData(user);
                            return sum + data.totalWithdraw;
                        }, 0).toFixed(2)}`,
                        icon: <span className="h-6 w-6 text-red-500 text-xl">₹</span>,
                        color: 'bg-red-50 text-red-700 border-red-100'
                    },
                    {
                        title: 'Balance Withdrawals',
                        value: `₹${users.reduce((sum, user) => {
                            const data = computeUserData(user);
                            return sum + data.totalBalance;
                        }, 0).toFixed(2)}`,
                        icon: <span className="h-6 w-6 text-blue-500 text-xl">₹</span>,
                        color: 'bg-blue-50 text-blue-700 border-blue-100'
                    },
                    {
                        title: 'Total Commission',
                        value: `₹${users.reduce((sum, user) => {
                            const data = computeUserData(user);
                            return sum + Math.abs(data.cmsnSettled);
                        }, 0).toFixed(2)}`,
                        icon: <span className="h-6 w-6 text-yellow-500 text-xl">₹</span>,
                        color: 'bg-yellow-50 text-yellow-700 border-yellow-100'
                    },
                    {
                        title: 'Net Settlement',
                        value: `₹${users.reduce((sum, user) => {
                            const data = computeUserData(user);
                            return sum + data.netSettlement;
                        }, 0).toFixed(2)}`,
                        icon: <span className="h-6 w-6 text-purple-500 text-xl">₹</span>,
                        color: 'bg-purple-50 text-purple-700 border-purple-100'
                    }
                ].map((stat, i) => (
                    <div key={i} className={`flex items-center justify-between p-4 rounded-lg border ${stat.color}`}>
                        <div>
                            <h3 className="text-sm font-medium opacity-80">{stat.title}</h3>
                            <p className="text-lg font-bold mt-1">{stat.value}</p>
                        </div>
                        <div className="p-2 rounded-full bg-white/70 shadow-sm">
                            {stat.icon}
                        </div>
                    </div>
                ))}
            </div>


            {/* Responsive table */}
            <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            {columns.map((column) => (
                                <th
                                    key={column.key}
                                    scope="col"
                                    className={`px-3 py-3.5 text-left text-sm font-semibold text-gray-900 ${column.sortable ? 'cursor-pointer hover:bg-gray-100' : ''}`}
                                >
                                    <div className="flex items-center gap-1">
                                        {column.label}

                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                        {groupByReferrer(sortedUsers).map(([referrerName, userList]) => (
                            <React.Fragment key={referrerName}>
                                {/* <tr className="bg-orange-100">
      <td colSpan={columns.length} className="text-sm font-bold px-3 py-2 text-orange-600 uppercase">
        Referred By: {referrerName}
      </td>
    </tr> */}
                                {(() => {
                                    const totals = userList.reduce(
                                        (acc, user) => {
                                            const data = computeUserData(user);
                                            acc.totalPaymentStatus += data.totalPaymentStatus;
                                            acc.totalDeposit += data.totalDeposit;
                                            acc.totalWithdraw += data.totalWithdraw;
                                            acc.totalBalance += data.totalBalance;
                                            acc.rawIdSettled += data.rawIdSettled;
                                            acc.cmsnSettled += Math.abs(data.cmsnSettled);
                                            acc.idSettled += data.idSettled;
                                            acc.netSettlement += data.netSettlement;
                                            return acc;
                                        },
                                        {
                                            totalPaymentStatus: 0,
                                            totalDeposit: 0,
                                            totalWithdraw: 0,
                                            totalBalance: 0,
                                            rawIdSettled: 0,
                                            cmsnSettled: 0,
                                            idSettled: 0,
                                            netSettlement: 0,
                                        }
                                    );

                                    return (
                                        <tr className="bg-orange-50 text-orange-900 font-semibold">
                                            <td className="px-3 py-2 text-sm" colSpan={3}>Total for {referrerName}</td>
                                            <td className="px-3 py-2 text-sm">{formatCurrency(totals.totalPaymentStatus)}</td>
                                            <td className="px-3 py-2 text-sm">-</td>
                                            <td className="px-3 py-2 text-sm">-</td>
                                            <td className="px-3 py-2 text-sm">{formatCurrency(totals.totalDeposit)}</td>
                                            <td className="px-3 py-2 text-sm">{formatCurrency(totals.totalWithdraw)}</td>
                                            <td className="px-3 py-2 text-sm">{formatCurrency(totals.totalBalance)}</td>
                                            <td className="px-3 py-2 text-sm">{formatCurrency(totals.rawIdSettled)}</td>
                                            <td className="px-3 py-2 text-sm">{formatCurrency(totals.cmsnSettled)}</td>
                                            <td className="px-3 py-2 text-sm">{formatCurrency(totals.idSettled)}</td>
                                            <td className="px-3 py-2 text-sm">{formatCurrency(totals.netSettlement)}</td>
                                        </tr>
                                    );
                                })()}

                                {userList.map((user, idx) => {
                                    const data = computeUserData(user);
                                    return (
                                        <tr
                                            key={user.userId}
                                            className={`${hoveredRow === user.userId ? 'bg-orange-50' : 'hover:bg-gray-50'} transition-colors`}
                                            onMouseEnter={() => setHoveredRow(user.userId)}
                                            onMouseLeave={() => setHoveredRow(null)}
                                        >
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{idx + 1}</td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">{data.name || 'N/A'}</td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">{data.userId}</td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{formatCurrency(data.totalPaymentStatus)}</td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{data.idPercentage}%</td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{data.commission}%</td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-green-600 font-medium">{formatCurrency(data.totalDeposit)}</td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-red-600 font-medium">{formatCurrency(data.totalWithdraw)}</td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-blue-600 font-medium">{formatCurrency(data.totalBalance)}</td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{formatCurrency(data.rawIdSettled)}</td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-yellow-600">{formatCurrency(Math.abs(data.cmsnSettled))}</td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{formatCurrency(data.idSettled)}</td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-purple-600">{formatCurrency(data.netSettlement)}</td>
                                        </tr>
                                    );
                                })}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6 mt-8">
  <h3 className="text-xl font-bold text-gray-800 mb-4">🔹 Grouped Totals by <span className="text-orange-600">Name</span></h3>
  <div className="overflow-x-auto">
    <table className="min-w-full text-sm divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-4 py-2 text-left">Name</th>
          <th className="px-4 py-2 text-left">Users</th>
          <th className="px-4 py-2 text-left">Deposit</th>
          <th className="px-4 py-2 text-left">Withdraw</th>
          <th className="px-4 py-2 text-left">Balance</th>
          <th className="px-4 py-2 text-left">Commission</th>
          <th className="px-4 py-2 text-left">Net Settlement</th>
        </tr>
      </thead>
      <tbody>
        {groupByName(users).map(([name, group]) => {
          const totals = group.reduce((acc, user) => {
            const data = computeUserData(user);
            acc.count += 1;
            acc.totalDeposit += data.totalDeposit;
            acc.totalWithdraw += data.totalWithdraw;
            acc.totalBalance += data.totalBalance;
            acc.cmsnSettled += Math.abs(data.cmsnSettled);
            acc.netSettlement += data.netSettlement;
            return acc;
          }, {
            count: 0,
            totalDeposit: 0,
            totalWithdraw: 0,
            totalBalance: 0,
            cmsnSettled: 0,
            netSettlement: 0
          });

          return (
            <tr key={name} className="hover:bg-gray-50">
              <td className="px-4 py-2 font-medium">{name}</td>
              <td className="px-4 py-2">{totals.count}</td>
              <td className="px-4 py-2 text-green-600">{formatCurrency(totals.totalDeposit)}</td>
              <td className="px-4 py-2 text-red-600">{formatCurrency(totals.totalWithdraw)}</td>
              <td className="px-4 py-2 text-blue-600">{formatCurrency(totals.totalBalance)}</td>
              <td className="px-4 py-2 text-yellow-600">{formatCurrency(totals.cmsnSettled)}</td>
              <td className="px-4 py-2 text-purple-600">{formatCurrency(totals.netSettlement)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
</div>

            {/* Footer */}
            <div className="mt-4 flex justify-between items-center text-gray-500 text-sm">
                <p>Showing {sortedUsers.length} of {users.length} users</p>
                <p>Last updated: {new Date().toLocaleDateString()}</p>
            </div>
        </div>
    );
};

export default AllUsersTable;